import { Link, useNavigate } from 'react-router-dom';
import Icon from './Icon.jsx';
import ProductImage from './ProductImage.jsx';
import { useCart } from '../../context/CartContext.jsx';
import { useToast } from '../../context/ToastContext.jsx';

/**
 * 商品卡片，Home / Shop / 相關推薦 共用。
 * 點擊卡片導向 /product/:id；點購物車按鈕加入購物車。
 *
 * @param product  { id, name, price, image? }  image 為 svg symbol id（預設 'dart-full'）
 */
function ProductCard({ product }) {
  const { addToCart } = useCart();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // 對應新 schema：image 來自 images 陣列；舊資料 fallback 到 product.image
  const imageId = (product.images && product.images[0]) || product.image || 'dart-full';

  // 後端回傳 priceMin（價格區間最低價）；mock 舊資料 fallback 到 product.price
  const price = product.priceMin ?? product.price;

  // 有價格區間（不同規格不同價）時顯示範圍，否則單一價
  const priceLabel = (() => {
    const min = product.priceMin ?? product.price;
    const max = product.priceMax ?? product.price;
    if (min == null) return product.price;
    if (max != null && max !== min) {
      return `NT$${min.toLocaleString()}~${max.toLocaleString()}`;
    }
    return `NT$${min.toLocaleString()}`;
  })();

  const variants = product.variants || [];

  const handleAdd = (e) => {
    e.preventDefault();
    // 多規格商品：導去詳情頁讓使用者選規格
    if (variants.length > 1) {
      navigate(`/product/${product.id}`);
      showToast('請選擇規格', 'info', 2000);
      return;
    }
    const v = variants[0];
    // 售完就不讓加（有 stock 數字才檢查；詳情頁與購物車還有後續防線）
    if (v && typeof v.stock === 'number' && v.stock <= 0) {
      showToast('此商品已售完', 'error', 2500);
      return;
    }
    if (!v) {
      // 沒有 variant 資訊（mock 舊資料）→ 退回用商品層加入
      addToCart({ lineId: product.id, productId: product.id, name: product.name, price, image: imageId });
    } else {
      addToCart({
        lineId: v.id,
        productId: product.id,
        variantId: v.id,
        sku: v.sku,
        name: product.name,
        price: v.price ?? price,
        image: imageId,
        tags: (product.optionTypes || []).map(t => v.attrs?.[t]).filter(Boolean),
      });
    }
    showToast(`已加入購物車：${product.name.slice(0, 20)}…`, 'success');
  };

  const tag = product.tags?.includes('new')  ? 'NEW'
            : product.tags?.includes('best') ? 'HOT'
            : product.tags?.includes('sale') ? 'SALE'
            : null;

  return (
    <article className="product">
      {tag && <span className="product-tag-pill">{tag}</span>}
      <Link to={`/product/${product.id}`} className="product-image">
        <ProductImage src={imageId} alt={product.name} svgStyle={{ height: '90%' }} />
      </Link>
      <Link to={`/product/${product.id}`} style={{ textDecoration: 'none', flex: 1 }}>
        <h3 className="product-name">{product.name}</h3>
      </Link>
      <div className="product-foot">
        <span className="product-price">
          {typeof priceLabel === 'number' ? `NT$${priceLabel.toLocaleString()}` : priceLabel}
        </span>
        <button className="cart-btn" aria-label="加入購物車" onClick={handleAdd}>
          <Icon name="cart" size={18} />
        </button>
      </div>
    </article>
  );
}

export default ProductCard;
